import { z } from 'zod'

export const magazineStatusSchema = z.enum(['draft', 'published'])
export type MagazineStatus = z.infer<typeof magazineStatusSchema>

export const magazineModeSchema = z.enum(['magazine', 'productImages', 'faq'])
export type MagazineMode = z.infer<typeof magazineModeSchema>

export const magazinePageTemplateSchema = z.enum([
  'coverGrid',
  'editorialIndex',
  'splitArticle',
  'imageEssay',
  'heroFeature',
  'productEditorial',
  'productImage',
  'collage',
  'pinboard',
  'faqText',
])
export type MagazinePageTemplate = z.infer<typeof magazinePageTemplateSchema>

export const magazineImageLayoutSchema = z.enum([
  'standard',
  'wide',
  'fullscreen',
  'hero',
  'portrait',
  'inset',
  'circle',
  'cutout',
])
export type MagazineImageLayout = z.infer<typeof magazineImageLayoutSchema>

export const magazineTextPositionSchema = z.enum([
  'auto',
  'topLeft',
  'topRight',
  'center',
  'bottomLeft',
  'bottomRight',
  'leftColumn',
  'rightColumn',
])
export type MagazineTextPosition = z.infer<typeof magazineTextPositionSchema>

export const magazineOverlayPositionSchema = z.enum([
  'topLeft',
  'topCenter',
  'topRight',
  'middleLeft',
  'middleCenter',
  'middleRight',
  'bottomLeft',
  'bottomCenter',
  'bottomRight',
])
export type MagazineOverlayPosition = z.infer<typeof magazineOverlayPositionSchema>

export const magazineImageCaptionPositionSchema = z.enum([
  'auto',
  'insideBottom',
  'insideTop',
  'outside',
  'hidden',
])
export type MagazineImageCaptionPosition = z.infer<typeof magazineImageCaptionPositionSchema>

export const magazineImageHotspotSchema = z.object({
  x: z.number().default(50),
  y: z.number().default(50),
  productId: z.number(),
  productKey: z.string().optional(),
  label: z.string().optional(),
})
export type MagazineImageHotspot = z.infer<typeof magazineImageHotspotSchema>

export const magazineImageSchema = z.object({
  src: z.string().optional(),
  alt: z.string().optional(),
  caption: z.string().optional(),
  aiGenerated: z.boolean().optional(),
  layout: magazineImageLayoutSchema.default('standard'),
  captionPosition: magazineImageCaptionPositionSchema.default('auto'),
  overlapX: z.number().default(0),
  overlapY: z.number().default(0),
  scale: z.number().default(100),
  hotspots: z.array(magazineImageHotspotSchema).default([]),
})
export type MagazineImage = z.infer<typeof magazineImageSchema>

export const magazineProductRefSchema = z.object({
  productId: z.number(),
  productKey: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  badge: z.string().optional(),
  showPrice: z.boolean().default(true),
})
export type MagazineProductRef = z.infer<typeof magazineProductRefSchema>

export const magazinePageLogoSchema = z.object({
  source: z.enum(['none', 'media', 'shop']).default('none'),
  src: z.string().optional(),
  alt: z.string().optional(),
  aiGenerated: z.boolean().optional(),
  width: z.number().default(180),
  position: magazineOverlayPositionSchema.default('topRight'),
})
export type MagazinePageLogo = z.infer<typeof magazinePageLogoSchema>

export const magazinePageTextLineSchema = z.object({
  text: z.string().optional(),
  position: magazineOverlayPositionSchema.default('bottomLeft'),
})
export type MagazinePageTextLine = z.infer<typeof magazinePageTextLineSchema>

export const magazineTextStyleSchema = z.object({
  fontFamily: z.string().optional(),
  fontSize: z.number().optional(),
  fontWeight: z.number().optional(),
  color: z.string().optional(),
})
export type MagazineTextStyle = z.infer<typeof magazineTextStyleSchema>

export const magazinePageTextStylesSchema = z.object({
  kicker: magazineTextStyleSchema.optional(),
  title: magazineTextStyleSchema.optional(),
  subtitle: magazineTextStyleSchema.optional(),
  body: magazineTextStyleSchema.optional(),
  quote: magazineTextStyleSchema.optional(),
}).default({})
export type MagazinePageTextStyles = z.infer<typeof magazinePageTextStylesSchema>

export const magazinePageSchema = z.object({
  id: z.string(),
  template: magazinePageTemplateSchema,
  kicker: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  body: z.string().optional(),
  quote: z.string().optional(),
  author: z.string().optional(),
  brandId: z.number().optional(),
  menuId: z.number().optional(),
  textPosition: magazineTextPositionSchema.default('auto'),
  images: z.array(magazineImageSchema).default([]),
  products: z.array(magazineProductRefSchema).default([]),
  backgroundColor: z.string().optional(),
  accentColor: z.string().optional(),
  textColor: z.string().optional(),
  heightVh: z.number().default(100),
  logo: magazinePageLogoSchema.optional(),
  textLine: magazinePageTextLineSchema.optional(),
  textStyles: magazinePageTextStylesSchema,
})
export type MagazinePage = z.infer<typeof magazinePageSchema>

export const magazineOverviewDataSchema = z.object({
  title: z.string().default('Magazin'),
  subtitle: z.string().optional(),
  description: z.string().optional(),
  heroImage: magazineImageSchema.optional(),
  metaTitle: z.string().optional(),
  metaDescription: z.string().optional(),
  tagTitle: z.string().optional(),
})
export type MagazineOverviewData = z.infer<typeof magazineOverviewDataSchema>

export const magazineOverviewSettingsSchema = magazineOverviewDataSchema.extend({
  shop: z.number(),
  created: z.date(),
  changed: z.date(),
})
export type MagazineOverviewSettings = z.infer<typeof magazineOverviewSettingsSchema>

export const magazineDataSchema = z.object({
  title: z.string(),
  slug: z.string(),
  issue: z.string().optional(),
  subtitle: z.string().optional(),
  excerpt: z.string().optional(),
  coverImage: magazineImageSchema.optional(),
  tags: z.array(z.string()).default([]),
  shops: z.array(z.number()).default([]),
  status: magazineStatusSchema.default('draft'),
  mode: magazineModeSchema.default('magazine'),
  onlineFrom: z.date().optional(),
  onlineTo: z.date().optional(),
  pages: z.array(magazinePageSchema).default([]),
  created: z.date(),
  changed: z.date(),
  deleted: z.literal(true).optional(),
})
export type MagazineData = z.infer<typeof magazineDataSchema>

export const magazineSchema = magazineDataSchema.extend({
  id: z.number(),
})
export type Magazine = z.infer<typeof magazineSchema>
